'use client';

import { forwardRef, ForwardedRef, useEffect, useState } from 'react';
import classNames from 'classnames';
import './css/Error.scss';

export interface ErrorProps extends React.HtmlHTMLAttributes<HTMLElement> {
  isErrorAnnounced?: boolean;
  /**
   * Localized aria-label for the error icon. Defaults to English.
   */
  iconAriaLabel?: string;
}

export const Error = forwardRef(
  (props: ErrorProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      children,
      className,
      id,
      isErrorAnnounced = true,
      iconAriaLabel = 'Error: ',
      ...spreadProps
    } = props;

    const [isAriaLiveRendered, setIsAriaLiveRendered] = useState(false);

    useEffect(() => {
      // Set the state to true after the component mounts
      const timer = setTimeout(() => {
        setIsAriaLiveRendered(true);
      }, 10);

      return () => clearTimeout(timer);
    }, []);

    const errorClass = classNames({
      'ps-error ': true,
      [`${className}`]: className,
    });

    return (
      <div
        {...spreadProps}
        id={id}
        aria-atomic={isErrorAnnounced ? 'true' : undefined}
        aria-live={isErrorAnnounced ? 'polite' : undefined}
        className={errorClass}
        ref={ref}
      >
        {isAriaLiveRendered && (
          <>
            <svg
              id={`${id}-icon`}
              className="ps-error-icon"
              fill="none"
              height="24"
              focusable="false"
              role="img"
              aria-label={iconAriaLabel}
              viewBox="0 0 16 24"
              width="16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.99998 16C7.69998 16 7.49998 15.9 7.39998 15.7C7.19998 15.5 7.19998 15.3 7.19998 15.1C7.19998 14.8 7.29998 14.6 7.39998 14.5C7.59998 14.3 7.79998 14.3 7.99998 14.3C8.29998 14.3 8.49998 14.4 8.59998 14.5C8.79998 14.7 8.79998 14.9 8.79998 15.1C8.79998 15.4 8.69998 15.6 8.59998 15.7C8.49998 15.9 8.29998 16 7.99998 16ZM7.39998 13.4L7.09998 8H8.79998L8.59998 13.4H7.39998Z"
                fill="#DB3321"
              />
              <path
                d="M8 5C11.9 5 15 8.1 15 12C15 15.9 11.9 19 8 19C4.1 19 1 15.9 1 12C1 8.1 4.1 5 8 5ZM8 4C3.6 4 0 7.6 0 12C0 16.4 3.6 20 8 20C12.4 20 16 16.4 16 12C16 7.6 12.4 4 8 4Z"
                fill="#DB3321"
              />
            </svg>
            {children}
          </>
        )}
      </div>
    );
  }
);
